import { useEffect } from 'react';
import config from '../config';

const AppMeta = () => {
  useEffect(() => {
    // Aggiorna il titolo della pagina
    document.title = config.app.title;

    // Funzione per aggiornare o creare meta tag
    const updateMetaTag = (name, content, attribute = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Funzione per aggiornare link tag
    const updateLinkTag = (rel, href, sizes = null) => {
      let link = document.querySelector(`link[rel="${rel}"]${sizes ? `[sizes="${sizes}"]` : ''}`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        if (sizes) link.setAttribute('sizes', sizes);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Aggiorna meta tag per PWA
    updateMetaTag('apple-mobile-web-app-title', config.app.shortName);
    updateMetaTag('application-name', config.app.name);
    updateMetaTag('description', config.app.description);
    updateMetaTag('theme-color', config.app.themeColor);
    
    // Meta tag Open Graph per condivisione social
    updateMetaTag('og:title', config.app.title, 'property');
    updateMetaTag('og:description', config.app.description, 'property');
    updateMetaTag('og:site_name', config.app.name, 'property');
    updateMetaTag('og:type', 'website', 'property');
    updateMetaTag('og:url', config.app.url, 'property');
    updateMetaTag('og:image', config.assets.logo192, 'property');

    // Aggiorna favicon dinamicamente
    updateLinkTag('icon', config.assets.favicon);
    updateLinkTag('apple-touch-icon', config.assets.logo192);
    updateLinkTag('apple-touch-icon', config.assets.logo192, '192x192');
    updateLinkTag('apple-touch-icon', config.assets.logo512, '512x512');
    
    // JSON-LD per SEO
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": config.app.name,
      "description": config.app.description,
      "url": config.app.url,
      "applicationCategory": "TravelApplication",
      "operatingSystem": "Any",
      "image": config.assets.logo512
    };
    
    // Rimuovi script JSON-LD esistente se presente
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Aggiungi nuovo script JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Genera manifest dinamico
    const generateDynamicManifest = () => {
      const manifest = {
        "id": ".",
        "short_name": config.app.shortName,
        "name": config.app.name,
        "description": config.app.description,
        "lang": config.app.lang,
        "dir": "ltr",
        "icons": [
          {
            "src": config.assets.favicon,
            "sizes": "256x256",
            "type": "image/x-icon",
            "purpose": "any"
          },
          {
            "src": config.assets.logo192,
            "type": "image/png",
            "sizes": "192x192",
            "purpose": "any"
          },
          {
            "src": config.assets.logo512,
            "type": "image/png",
            "sizes": "512x512",
            "purpose": "any"
          },
          {
            "src": config.assets.maskableIcon,
            "sizes": "512x512",
            "type": "image/png",
            "purpose": "maskable"
          }
        ],
        "start_url": process.env.REACT_APP_HOMEPAGE || "/",
        "scope": process.env.REACT_APP_HOMEPAGE || "/",
        "display": "standalone",
        "orientation": config.app.orientation,
        "theme_color": config.app.themeColor,
        "background_color": config.app.backgroundColor,
        "prefer_related_applications": false,
        "categories": config.app.categories,
        "edge_side_panel": {
          "preferred_width": 480
        },
        "launch_handler": {
          "client_mode": "navigate-existing"
        }
      };

      // Includi screenshots SOLO se configurati nelle variabili d'ambiente
      const screenshots = [];
      
      if (process.env.REACT_APP_SCREENSHOT_DESKTOP) {
        screenshots.push({
          "src": process.env.REACT_APP_SCREENSHOT_DESKTOP,
          "sizes": process.env.REACT_APP_SCREENSHOT_DESKTOP_SIZE || "1915x902",
          "type": "image/png",
          "form_factor": "wide",
          "label": "Homescreen app"
        });
      }
      
      if (process.env.REACT_APP_SCREENSHOT_MOBILE) {
        screenshots.push({
          "src": process.env.REACT_APP_SCREENSHOT_MOBILE,
          "sizes": process.env.REACT_APP_SCREENSHOT_MOBILE_SIZE || "385x856",
          "type": "image/png",
          "label": "Homescreen app on mobile"
        });
      }
      
      // Aggiungi screenshots al manifest solo se ce ne sono
      if (screenshots.length > 0) {
        manifest.screenshots = screenshots;
      }

      return manifest;
    };

    // Aggiorna il manifest dinamicamente
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const dynamicManifest = generateDynamicManifest();
      
      // Crea un blob URL per il manifest dinamico
      const manifestBlob = new Blob([JSON.stringify(dynamicManifest, null, 2)], {
        type: 'application/json'
      });
      const manifestUrl = URL.createObjectURL(manifestBlob);
      manifestLink.href = manifestUrl;
    }
    
  }, []);

  return null;
};

export default AppMeta;